/**
 * AddressAutocomplete (ADDR-LIVE, Seth 2026-08-28: "I thought we were going to
 * have it be a live real time search?") — the street-address input, upgraded:
 * type and Google Places suggests real addresses as you go; pick one and the
 * city/state/zip fill themselves. Shared by Add Customer AND Add Property so
 * address entry feels identical everywhere.
 *
 * Same VITE_GOOGLE_MAPS_KEY the 360's photos + geocode check use — the Places
 * library loads once, on demand. If the SDK can't load (no key, Places API not
 * enabled, offline), this renders as a plain input and the caller's
 * "Check Address With Google" fallback still works. Propose, never force:
 * every field stays hand-editable after a pick.
 */
import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";

export interface ResolvedAddress {
  add1: string;
  city: string;
  state: string;
  zip: string;
}

// ---- minimal typings for the slice of the Maps SDK we touch ----
interface GPrediction { description: string; place_id: string }
interface GAutocompleteSvc {
  getPlacePredictions: (
    req: { input: string; componentRestrictions?: { country: string }; types?: string[] },
    cb: (preds: GPrediction[] | null) => void,
  ) => void;
}
interface GGeocoderResult {
  address_components?: { types: string[]; long_name: string; short_name: string }[];
}
interface GGeocoder {
  geocode: (
    req: { placeId: string },
    cb: (results: GGeocoderResult[] | null, status: string) => void,
  ) => void;
}
interface GMapsNs {
  importLibrary?: (name: string) => Promise<unknown>;
  places?: { AutocompleteService: new () => GAutocompleteSvc };
  Geocoder?: new () => GGeocoder;
}
interface GWindow { google?: { maps?: GMapsNs } }

let sdkPromise: Promise<boolean> | null = null;
function loadPlacesSdk(): Promise<boolean> {
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  if (!key) return Promise.resolve(false);
  const w = window as unknown as GWindow;
  if (w.google?.maps?.places) return Promise.resolve(true);
  if (!sdkPromise) {
    sdkPromise = new Promise<boolean>((resolve) => {
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&v=weekly`;
      s.async = true;
      s.onload = async () => {
        try {
          const g = (window as unknown as GWindow).google?.maps;
          if (g?.importLibrary) await g.importLibrary("places");
          resolve(!!(window as unknown as GWindow).google?.maps?.places);
        } catch {
          resolve(false);
        }
      };
      s.onerror = () => resolve(false);
      document.head.appendChild(s);
    });
  }
  return sdkPromise;
}

function componentsToAddress(r: GGeocoderResult): ResolvedAddress {
  const comp = (type: string, short = false): string => {
    const c = r.address_components?.find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : "";
  };
  return {
    add1: [comp("street_number"), comp("route")].filter(Boolean).join(" "),
    city: comp("locality") || comp("sublocality") || "",
    state: comp("administrative_area_level_1", true) || "MN",
    zip: comp("postal_code"),
  };
}

export function AddressAutocomplete({
  value,
  onChange,
  onResolved,
  placeholder,
  autoFocus,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Fired when the user PICKS a suggestion — city/state/zip included. */
  onResolved: (a: ResolvedAddress) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Input classes — pass the caller's own input styling so it blends in. */
  className?: string;
}) {
  const [ready, setReady] = useState(false);
  const [preds, setPreds] = useState<GPrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const svcRef = useRef<GAutocompleteSvc | null>(null);
  const timer = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    void loadPlacesSdk().then((ok) => {
      if (!alive) return;
      if (ok) {
        const g = (window as unknown as GWindow).google!.maps!;
        svcRef.current = new g.places!.AutocompleteService();
        setReady(true);
      }
    });
    return () => { alive = false; };
  }, []);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = (input: string) => {
    if (!ready || !svcRef.current || input.trim().length < 4) { setPreds([]); setOpen(false); return; }
    svcRef.current.getPlacePredictions(
      { input, componentRestrictions: { country: "us" }, types: ["address"] },
      (p) => { setPreds(p ?? []); setOpen((p ?? []).length > 0); setHi(0); },
    );
  };

  const handleChange = (v: string) => {
    onChange(v);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => search(v), 280);
  };

  const pick = (p: GPrediction) => {
    setOpen(false);
    setPreds([]);
    const g = (window as unknown as GWindow).google?.maps;
    if (!g?.Geocoder) { onChange(p.description); return; }
    new g.Geocoder().geocode({ placeId: p.place_id }, (results, status) => {
      const r = status === "OK" ? results?.[0] : null;
      if (r) {
        const a = componentsToAddress(r);
        onChange(a.add1 || p.description);
        onResolved(a);
      } else {
        onChange(p.description);
      }
    });
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => { if (preds.length > 0) setOpen(true); }}
        onKeyDown={(e) => {
          if (!open || preds.length === 0) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, preds.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter") { e.preventDefault(); pick(preds[hi]); }
          else if (e.key === "Escape") setOpen(false);
        }}
        placeholder={placeholder ?? (ready ? "Start typing the address — Google suggests as you go" : "Street address")}
        autoFocus={autoFocus}
        className={className}
        autoComplete="off"
      />
      {open && preds.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {preds.map((p, i) => (
            <li key={p.place_id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                onMouseEnter={() => setHi(i)}
                className={
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 " +
                  (i === hi ? "bg-brand-50" : "")
                }
              >
                <MapPin className="h-3 w-3 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate">{p.description}</span>
              </button>
            </li>
          ))}
          <li className="px-3 pt-1 text-right text-[9px] text-slate-300">powered by Google</li>
        </ul>
      )}
    </div>
  );
}
